import type { FC } from 'react';
import React from 'react';

import { Box, Inline, Stack, xcss, Anchor, Pressable } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import type { PremiumFeature, PremiumFeatureItem } from './PremiumFeatureTypes';

export type PremiumFeatureBoxComponentProps = {
	premiumFeatureItem: PremiumFeatureItem;
	setFeatureShowing: React.Dispatch<React.SetStateAction<PremiumFeature>>;
	featureShowing: PremiumFeature;
	onFeatureLinkClick: (feature: PremiumFeature) => void;
};

const featureBoxStyle = xcss({
	borderRadius: '3px',
});
const featureTextStyle = xcss({
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},
});
const anchorStyle = xcss({
	textDecorationLine: 'none',
});

export const PremiumFeatureBoxComponent: FC<PremiumFeatureBoxComponentProps> = ({
	premiumFeatureItem,
	setFeatureShowing,
	featureShowing,
	onFeatureLinkClick,
}) => {
	const { icon: Icon } = premiumFeatureItem;

	return (
		<>
			{premiumFeatureItem.feature !== featureShowing && (
				<Pressable
					padding="space.200"
					backgroundColor="color.background.neutral.subtle"
					onClick={() => {
						setFeatureShowing(premiumFeatureItem.feature);
					}}
					xcss={featureTextStyle}
				>
					<Inline space="space.150" alignBlock="center" grow="fill">
						<Box paddingBlockStart="space.050" paddingInline="space.050">
							<Icon label={premiumFeatureItem.title} />
						</Box>
						<Box>
							<Heading size="xsmall">{premiumFeatureItem.title}</Heading>
						</Box>
					</Inline>
				</Pressable>
			)}
			{premiumFeatureItem.feature === featureShowing && (
				<Box
					padding="space.200"
					backgroundColor="color.background.accent.blue.subtlest"
					xcss={featureBoxStyle}
				>
					<Inline space="space.150">
						<Box paddingBlockStart="space.050" paddingInline="space.050">
							<Icon label={premiumFeatureItem.title} color={token('color.icon.brand')} />
						</Box>
						<Stack space="space.100">
							<Box>
								<Heading size="xsmall">{premiumFeatureItem.title}</Heading>
							</Box>
							<Box>{premiumFeatureItem.description}</Box>
							<Box>
								<Anchor
									xcss={anchorStyle}
									href={premiumFeatureItem.actionLink}
									target="_blank"
									onClick={() => onFeatureLinkClick(premiumFeatureItem.feature)}
								>
									{premiumFeatureItem.action}
								</Anchor>
							</Box>
						</Stack>
					</Inline>
				</Box>
			)}
		</>
	);
};
